import React from 'react';
import { SEOHead, getOrganizationSchema } from '../components/common/SEOHead';
import { Breadcrumbs } from '../components/common/Breadcrumbs';
import { Award, Globe, Users, Package, Check } from 'lucide-react';

const WAREHOUSE_IMAGE = `${process.env.PUBLIC_URL}/about_us.png`;
const TEAM_IMAGE = 'https://images.unsplash.com/photo-1758691737158-18ffa31c0a46?crop=entropy&cs=srgb&fm=jpg&w=800&q=85';

export default function AboutPage() {
  const stats = [
    { icon: Award, value: '18+', label: 'Years Experience' },
    { icon: Globe, value: 'PAN India', label: 'Delivery' },
    { icon: Users, value: '500+', label: 'Happy Partners' },
    { icon: Package, value: '4000+', label: 'Products' },
  ];

  const whyPoints = [
    'Nearly 20 Years of Industry Excellence',
    '100% Authentic & Original Perfumes',
    '500+ Brands | 4,000+ Products',
    'Authorized Middle Eastern Distribution',
    'Reliable Inventory & Long-Term Partnerships',
  ];

  const milestones = [
    { year: '2007', title: 'Inspiration Abroad', description: 'Our founders immersed themselves in the fragrance cultures of Dubai and Thailand markets celebrated for Arabian oud, luxury retail, and global scent craftsmanship.' },
    { year: '2010', title: 'Vision Takes Shape', description: 'Inspired by Arabian richness and international elegance, the idea of bringing a refined fragrance destination to India began to take form.' },
    { year: '2013', title: 'Born in Ahmedabad', description: 'Silver Perfumes was brought to life in Ahmedabad as a wholesale home for branded perfumes, Arabic fragrances, attars, and oud.' },
    { year: '2018', title: 'Portfolio Depth', description: 'Expanded into a wider range of designer icons, niche creations, and Middle Eastern houses trusted by retailers nationwide.' },
    { year: '2023', title: 'A Destination Brand', description: 'Recognized as a destination for partners who value authenticity, character, and fragrances that leave a lasting impression.' },
  ];

  return (
    <>
      <SEOHead
        title="About Silver Perfumes - Heritage, Luxury & Fine Fragrance"
        description="Born from a journey through Dubai and Thailand and brought to life in Ahmedabad, Silver Perfumes offers branded perfumes, Arabic fragrances, premium attars, and oud."
        canonical="https://silverperfumes.thevinculum.org.in/about"
        schemaData={getOrganizationSchema()}
      />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-[#0A0A0A]" data-testid="about-hero">
        <div className="container-custom">
          <Breadcrumbs items={[{ label: 'About Us' }]} />
          <div className="mt-6 max-w-3xl">
            <span className="label-text mb-4 block">Our Story</span>
            <h1 className="heading-xl text-white mb-6">
              About Silver Perfumes
            </h1>
            <p className="text-gray-300 text-lg mb-4">
              Born from a journey through Dubai and Thailand, and brought to life in Ahmedabad, Silver Perfumes is a destination for those who appreciate the art of fine fragrance.
            </p>
            <p className="text-gray-300 text-lg">
              Where heritage, luxury, and fragrance come together.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-white border-b" data-testid="about-stats">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <stat.icon className="h-8 w-8 text-[#C0C4CC] mx-auto mb-3" />
                <div className="text-3xl font-bold text-[#0A0A0A] mb-1 font-serif">
                  {stat.value}
                </div>
                <div className="text-gray-500 text-sm uppercase tracking-widest">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="section-padding bg-white" data-testid="about-story">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="heading-lg mb-6">A Journey of Scent &amp; Culture</h2>
              <div className="silver-separator mb-6" />
              <div className="space-y-5 text-gray-600 leading-relaxed">
                <p>
                  Born from a journey through Dubai and Thailand, and brought to life in Ahmedabad, Silver Perfumes is a destination for those who appreciate the art of fine fragrance.
                </p>
                <p>
                  Inspired by the richness of Arabian perfumery and the elegance of global scent culture, we offer a refined collection of branded perfumes, Arabic fragrances, premium attars, oud, and more curated for partners who seek depth, authenticity, and distinction.
                </p>
                <p>
                  Every fragrance we select is chosen to evoke character, confidence, and lasting impression. From opulent oriental compositions to contemporary designer signatures, our assortment is built to elevate retail shelves and wholesale portfolios alike.
                </p>
                <p className="text-[#0A0A0A] font-medium">
                  Silver Perfumes where heritage, luxury, and fragrance come together.
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src={WAREHOUSE_IMAGE}
                alt="Silver Perfumes store"
                className="w-full aspect-[4/3] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-[#0A0A0A] text-white p-6 max-w-xs">
                <p className="text-sm italic">
                  &quot;Every fragrance we select is chosen to evoke character, confidence, and lasting impression.&quot;
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Collection focus */}
      <section className="section-padding bg-white border-t border-gray-100" data-testid="about-collection">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <span className="label-text mb-4 block">Our Collection</span>
            <h2 className="heading-lg mb-4">What We Offer</h2>
            <div className="silver-separator mx-auto mb-6" />
            <p className="text-gray-600">
              A refined selection shaped by Arabian heritage and global elegance ready for retailers, wholesalers, and fragrance specialists.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: 'Branded Perfumes', desc: 'Designer and contemporary signatures trusted worldwide' },
              { title: 'Arabic Fragrances', desc: 'Rich compositions rooted in Middle Eastern perfume culture' },
              { title: 'Premium Attars', desc: 'Concentrated oils crafted for lasting character' },
              { title: 'Oud & Oriental', desc: 'Opulent oud and oriental masterpieces for discerning shelves' },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-[#F4F4F5] p-6 text-center border border-transparent hover:border-[#C0C4CC]/40 transition-colors"
              >
                <h3 className="heading-sm mb-2">{item.title}</h3>
                <p className="text-gray-500 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Serving the Fragrance Elite */}
      <section className="section-padding bg-[#F4F4F5]" data-testid="about-fragrance-elite">
        <div className="container-custom">
          <div className="text-center mb-10">
            <span className="label-text mb-4 block">Our Partners</span>
            <h2 className="heading-lg mb-4">Serving the Fragrance Elite</h2>
            <div className="silver-separator mx-auto mb-6" />
            <p className="text-gray-600 max-w-2xl mx-auto">
              Silver Perfumes partners with retailers and distributors who value consistency, discretion, and premium service.
            </p>
          </div>
            
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: 'Luxury Perfume Boutiques', desc: 'Premium retail partners' },
              { title: 'High-End Retailers & Department Stores', desc: 'National & international retail' },
              { title: 'E-commerce & Global Marketplaces', desc: 'Online fragrance specialists' },
              { title: 'Wholesalers & Regional Distributors', desc: 'B2B distribution network' },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-white rounded-lg border border-gray-100 p-6 text-center shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-10 h-10 rounded-full bg-[#0A0A0A]/5 flex items-center justify-center mx-auto mb-4">
                  <span className="text-[#C0C4CC] font-serif text-lg font-semibold">{i + 1}</span>
                </div>
                <h3 className="heading-sm mb-2">{item.title}</h3>
                <p className="text-gray-500 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-center mt-6 text-gray-600 max-w-2xl mx-auto">
            Our clientele values consistency, discretion, and premium service - standards we uphold in every transaction.
            </p>
        </div>
      </section>

      {/* Why Silver Perfumes */}
      <section className="section-padding bg-[#F4F4F5]" data-testid="about-values">
        <div className="container-custom">
          <div className="text-center mb-10">
            <span className="label-text mb-4 block">Why Choose Us</span>
            <h2 className="heading-lg mb-4">Why Silver Perfumes</h2>
            <div className="silver-separator mx-auto mb-6" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center">
            {whyPoints.slice(0, 3).map((point, index) => (
              <div
                key={index}
                className="bg-white rounded-lg border border-gray-100 p-6 text-center shadow-sm hover:shadow-md transition-shadow w-full max-w-sm"
              >
                <div className="w-10 h-10 rounded-full bg-[#C0C4CC]/20 flex items-center justify-center mx-auto mb-4">
                  <Check className="h-5 w-5 text-[#C0C4CC]" strokeWidth={2.5} />
                </div>
                <p className="text-gray-700 font-medium leading-snug">{point}</p>
              </div>
            ))}
            <div className="w-full lg:col-span-3 flex flex-col sm:flex-row flex-wrap justify-center gap-6 lg:gap-6">
              {whyPoints.slice(3, 5).map((point, index) => (
                <div
                  key={index + 3}
                  className="bg-white rounded-lg border border-gray-100 p-6 text-center shadow-sm hover:shadow-md transition-shadow w-full max-w-sm"
                >
                  <div className="w-10 h-10 rounded-full bg-[#C0C4CC]/20 flex items-center justify-center mx-auto mb-4">
                    <Check className="h-5 w-5 text-[#C0C4CC]" strokeWidth={2.5} />
                  </div>
                  <p className="text-gray-700 font-medium leading-snug">{point}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="section-padding bg-white" data-testid="about-timeline">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="label-text mb-4 block">Our Journey</span>
            <h2 className="heading-lg">Milestones</h2>
          </div>
          <div className="max-w-3xl mx-auto">
            {milestones.map((milestone, index) => (
              <div key={index} className="flex gap-6 mb-8 last:mb-0">
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-full bg-[#0A0A0A] text-white flex items-center justify-center font-serif text-sm">
                    {milestone.year.slice(2)}
                  </div>
                  {index < milestones.length - 1 && (
                    <div className="w-px h-full bg-[#C0C4CC] mt-2" />
                  )}
                </div>
                <div className="pb-8">
                  <div className="text-[#C0C4CC] text-sm font-medium mb-1">{milestone.year}</div>
                  <h4 className="heading-sm mb-2">{milestone.title}</h4>
                  <p className="text-gray-600">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="section-padding bg-[#0A0A0A]" data-testid="about-team">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="label-text mb-4 block">Silver Perfumes</span>
              <h2 className="heading-lg text-white mb-6">
                Where Heritage, Luxury &amp; Fragrance Come Together
              </h2>
              <p className="text-gray-300 mb-4">
                From Ahmedabad to partners across India, we remain guided by the cultures that inspired us - Arabian richness, global elegance, and an uncompromising eye for fine fragrance.
              </p>
              <p className="text-gray-300 mb-8">
                For retailers and wholesalers who value authenticity and lasting impression, Silver Perfumes is more than a supplier. It is a destination.
              </p>
            </div>
            <div>
              <img
                src={TEAM_IMAGE}
                alt="Our Team"
                className="w-full aspect-[4/3] object-cover"
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
